import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { locations, primaryLocation } from "@/lib/locations-data";
import { useInView } from "@/hooks/use-in-view";

/**
 * Service-area map.
 *
 * Leaflet and its stylesheet are both loaded on demand, and only once the map
 * has scrolled close to the viewport. That keeps ~150KB of mapping library,
 * its CSS and a screenful of OpenStreetMap tiles off the critical path of the
 * home page, where the map sits several sections below the fold.
 */
const LocationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const [activeSlug, setActiveSlug] = useState<string>(primaryLocation.slug);
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>("300px");

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;

    const initMap = async () => {
      const [L] = await Promise.all([
        import("leaflet"),
        // Side-effect import: Vite emits this as its own stylesheet, so the
        // map CSS no longer rides along in the main bundle.
        import("leaflet/dist/leaflet.css"),
      ]);

      if (cancelled || !mapRef.current) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
      }).setView([primaryLocation.lat, primaryLocation.lng], 10);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // 35-mile radius circle (56,327 meters)
      L.circle([primaryLocation.lat, primaryLocation.lng], {
        radius: 56327,
        color: "hsl(355, 82%, 46%)",
        fillColor: "hsl(355, 82%, 46%)",
        fillOpacity: 0.08,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: '<div style="background:hsl(0,0%,10%);width:36px;height:36px;border-radius:50%;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>' +
          "</div>",
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      const cityIcon = L.divIcon({
        className: "custom-marker-city",
        html: '<div style="background:hsl(355,82%,46%);width:20px;height:20px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      locations.forEach((loc) => {
        const icon = loc.isPrimary ? primaryIcon : cityIcon;
        const label = `${loc.name}, ${loc.state}${loc.isPrimary ? " — headquarters" : ""}`;
        const marker = L.marker([loc.lat, loc.lng], { icon, title: label, alt: label })
          .addTo(map)
          .bindPopup(
            `<div style="padding:8px;font-family:sans-serif;min-width:180px;">
              <strong style="font-size:14px;color:hsl(355,82%,46%);">${loc.name}, ${loc.state}</strong>
              <p style="color:#666;font-size:12px;margin:4px 0 6px;">${loc.region}${loc.isPrimary ? " · HQ" : ""}</p>
              <a href="/locations/${loc.slug}" style="color:hsl(355,82%,46%);font-weight:600;font-size:12px;text-decoration:none;">View ${loc.name} location →</a>
            </div>`
          );
        // Leaflet gives divIcon markers role="button" and a tab stop but no
        // accessible name, so name them explicitly.
        marker.getElement()?.setAttribute("aria-label", label);
        markersRef.current[loc.slug] = marker;
      });
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [inView]);

  const handleLocationClick = (slug: string, lat: number, lng: number) => {
    setActiveSlug(slug);
    if (mapInstanceRef.current) {
      const zoom = slug === primaryLocation.slug ? 10 : 12;
      mapInstanceRef.current.flyTo([lat, lng], zoom, { duration: 1 });
      setTimeout(() => {
        markersRef.current[slug]?.openPopup();
      }, 800);
    }
  };

  return (
    <div ref={sectionRef} className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
      <div className="bg-primary px-5 py-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary-foreground" aria-hidden="true" />
        <span className="font-heading font-bold text-primary-foreground text-sm tracking-wide">
          Service Area — 35 Mile Radius from Lawrenceville, GA
        </span>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-[35%] lg:border-r border-border overflow-y-auto max-h-[350px] lg:max-h-[520px] scrollbar-hide">
          {locations.map((loc) => {
            const active = activeSlug === loc.slug;
            return (
              /* The focus button and the "View" link are siblings here — a link
                 nested inside a button is invalid markup and leaves the pair
                 impossible to operate predictably by keyboard. */
              <div
                key={loc.slug}
                className={`flex items-center justify-between border-b border-border border-l-4 transition-all ${
                  active ? "bg-primary/10 border-l-primary" : "border-l-transparent hover:bg-muted/50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleLocationClick(loc.slug, loc.lat, loc.lng)}
                  aria-pressed={active}
                  className="flex-1 min-w-0 text-left pl-5 pr-3 py-3 flex items-center gap-2.5"
                >
                  {loc.isPrimary ? (
                    <span className="h-3 w-3 rounded-full bg-secondary shrink-0 ring-2 ring-secondary/30" />
                  ) : (
                    <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
                  )}
                  <span className="min-w-0">
                    <span
                      className={`block font-heading font-bold text-sm tracking-tight ${
                        active ? "text-primary-dark" : "text-foreground"
                      }`}
                    >
                      {loc.name}, {loc.state}
                    </span>
                    <span className="block text-muted-foreground text-[11px]">
                      {loc.region}
                      {loc.isPrimary ? " · HQ" : ""}
                    </span>
                  </span>
                </button>
                <Link
                  to={`/locations/${loc.slug}`}
                  className="shrink-0 pr-5 pl-3 py-3 text-xs font-semibold text-primary-dark hover:text-primary whitespace-nowrap"
                >
                  View<span className="sr-only"> the {loc.name}, {loc.state} page</span> →
                </Link>
              </div>
            );
          })}
        </div>

        <div className="w-full lg:w-[65%] h-[400px] lg:h-[520px] relative" style={{ zIndex: 0 }}>
          <div ref={mapRef} className="w-full h-full bg-muted" style={{ zIndex: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default LocationMap;
